import PrintMethodToggle from '@/components/print/PrintMethodToggle';
/**
 * FG Re-Sticker Queue — bulk price-revision workflow.
 *
 * Mounted twice:
 *   • /admin/re-sticker-queue                  (Salem)
 *   • /sections/dispatch/:section/re-sticker   (per-section)
 *
 * The Salem mount passes `section=undefined`; the per-section mount
 * passes the slug from useParams. Backend `/api/price-revision/*`
 * branches collection lookup accordingly.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Upload, Filter, Printer, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  TrendingUp, TrendingDown, FileText, Loader2, Box, X, ChevronUp, ChevronDown, Usb,
  PackageOpen, Download, Search, Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { noRateMessage, usePricingChannels } from '@/hooks/usePricingChannels';
import * as printerApi from '@/services/printerApi';
import * as authApi from '@/services/authApi';
import { generateTsplFromLabelData, PRINTER_CONFIGS } from '@/utils/tsplGenerator';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const MAX_BATCH = 200;

const ReStickerQueue = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = slug ? SLUG_TO_DISPLAY[slug] : 'Salem (All FG)';
  const sectionParam = slug ? `?section=${slug}` : '';
  const backRoute = slug ? `/sections/dispatch/${slug}` : '/admin/dashboard';

  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [aggregations, setAggregations] = useState({ pending: 0, done: 0, failed: 0, filtered: 0 });
  const [pagination, setPagination] = useState({ skip: 0, limit: 200, total: 0 });
  const [selected, setSelected] = useState(new Set());
  const [showFilters, setShowFilters] = useState(true);

  // 2026-05-18 — Bin Status report state
  const [binStatusLoading, setBinStatusLoading] = useState(false);
  const [binStatusRows, setBinStatusRows] = useState([]);
  const [binStatusTotals, setBinStatusTotals] = useState({ bins: 0, total_packs: 0, total_unstickered: 0, total_clean: 0 });
  const [binStatusFilters, setBinStatusFilters] = useState({
    bin_code: '', hall: '', row: '', rack: '', level: '',
    has_unstickered_only: false,
  });
  const [binStatusExpanded, setBinStatusExpanded] = useState({});  // { bin_code: 'unstickered' | 'clean' | 'all' }
  const [binStatusPacks, setBinStatusPacks] = useState({});  // { bin_code: { packs, loading } }

  // Filters
  const DEFAULT_FILTERS = {
    hall: '', row: '', rack: '', level: '', bin_from: '', bin_to: '', velocity_class: '',
    sku: '', design_name: '', design_code: '', color: '', size: '', category4: '', brand: '',
    revision_id: '', pct_change_min: '', pct_change_max: '', abs_change_min: '', direction: '',
    status: 'pending', put_away_from: '', put_away_to: '',
  };
  // Draft = bound to inputs; Applied = what's actually sent to the backend.
  // Separation prevents one fetch per keystroke (which races + makes filters
  // appear "not exact" because the last fetch to complete wins).
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState('bin_sku');

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().slice(0, 10));
  const [uploading, setUploading] = useState(false);

  // Print batch dialog
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  // 2026-05-12: bin-scoped Revert Done dialog state.
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertBin, setRevertBin] = useState('');
  const [revertPreview, setRevertPreview] = useState(null);
  const [revertPreviewLoading, setRevertPreviewLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printProgress, setPrintProgress] = useState({ done: 0, total: 0, success: 0, failed: 0, log: [] });
  const [printAbortFn, setPrintAbortFn] = useState(null);

  // USB printer integration (mirrors SFGMainLabelPrinting / ComboAssemblyStation)
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(false);
  const [printerModel, setPrinterModel] = useState('TSC_TE210');
  const [pricingType, setPricingType] = useState('TN');
  const pricingChannels = usePricingChannels();
  const [templates, setTemplates] = useState([]);

  // Staff capture for wage payout — scanned at the start of each batch.
  const [batchStaffId, setBatchStaffId] = useState('');
  const [batchStaffName, setBatchStaffName] = useState('');
  const [staffPromptOpen, setStaffPromptOpen] = useState(false);
  const [staffValidating, setStaffValidating] = useState(false);

  // 2026-05-14 — Recover Stuck Packs (Admin escape hatch for HTTP-520
  // partial bulks where the new USN was minted + printed but the
  // mark-restickered step never landed).
  const [stuckDialogOpen, setStuckDialogOpen] = useState(false);
  const [stuckSubmitting, setStuckSubmitting] = useState(false);
  const [stuckPreview, setStuckPreview] = useState(null);
  const [stuckResult, setStuckResult] = useState(null);
  const [stuckWindowHours, setStuckWindowHours] = useState(48);

  // 2026-05-14 — Two-phase combo print dialog (product → swap roll → box).
  // 2026-05-14 (B) — Box-sticker printer must be EXPLICITLY selected on
  // this prompt; Continue is disabled until a device is connected.
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [phaseDialogComboCount, setPhaseDialogComboCount] = useState(0);
  const [phaseDialogResolve, setPhaseDialogResolve] = useState(null);
  const [phaseBoxDevice, setPhaseBoxDevice] = useState(null);
  const [phaseBoxConnecting, setPhaseBoxConnecting] = useState(false);
  // 2026-05-14 (C) — Template selectors on both prompts to fix alignment
  // issues. Phase A picks the product-sticker template; Phase B picks
  // the box-sticker template. Both default to the most-common
  // auto-resolved template in the batch.
  const [phaseBoxTemplate, setPhaseBoxTemplate] = useState('');
  const [phaseBoxAvailableTemplates, setPhaseBoxAvailableTemplates] = useState([]);

  const [phaseAOpen, setPhaseAOpen] = useState(false);
  const [phaseAResolve, setPhaseAResolve] = useState(null);
  const [phaseAPackCount, setPhaseAPackCount] = useState(0);
  const [phaseAComboCount, setPhaseAComboCount] = useState(0);
  const [phaseAProductTemplate, setPhaseAProductTemplate] = useState('');
  const [phaseAAvailableTemplates, setPhaseAAvailableTemplates] = useState([]);
  const [phaseADevice, setPhaseADevice] = useState(null);
  const [phaseAConnecting, setPhaseAConnecting] = useState(false);


  // Revision batches
  const [batches, setBatches] = useState([]);

  // Audit
  const [auditEvents, setAuditEvents] = useState([]);

  // Current-user perms for admin-only button gating.
  //   Super Admin (role ∈ {Admin, super_admin}) sees every top-bar action.
  //   Anyone with module access `fg_wms` + op `re-sticker-queue` sees ONLY
  //   the "Reprint Stickers" button (corrective work — safe for ops staff).
  //   The destructive top buttons (Refresh Stamps / Revert Done / Upload
  //   Price Revision CSV) require the additional `re-sticker-admin-actions`
  //   op grant or super-admin role.
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    authApi.getMe()
      .then((u) => setCurrentUser(u))
      .catch(() => setCurrentUser({}));
  }, []);
  // 2026-05-15 — REPLACE pool depth indicator. Surfaces ahead of time
  // whether a Print Batch will fail with "No packs were minted"
  // (which is almost always due to an empty REPLACE pool).
  const [replacePool, setReplacePool] = useState(null);
  useEffect(() => {
    const fetchPool = () => {
      import('@/services/apiClient')
        .then(({ default: api }) => api.get('/usn-replacement/pool-status'))
        .then((r) => setReplacePool(r.data))
        .catch(() => setReplacePool(null));
    };
    fetchPool();
    // Refresh every 60s so the count stays current during long sessions.
    const t = setInterval(fetchPool, 60000);
    return () => clearInterval(t);
  }, []);
  const isSuperAdmin = (() => {
    const r = (currentUser?.role || '').toLowerCase();
    return r === 'admin' || r === 'super_admin';
  })();
  const canRunAdminActions = isSuperAdmin || (
    Array.isArray(currentUser?.allowed_operations?.fg_wms)
    && currentUser.allowed_operations.fg_wms.includes('re-sticker-admin-actions')
  );

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(sectionParam ? { section: slug } : {});
      Object.entries(appliedFilters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params.append(k, v);
      });
      params.append('sort_by', sortBy);
      params.append('skip', pagination.skip);
      params.append('limit', pagination.limit);
      const res = await apiClient.get(`/price-revision/pending-restickers?${params}`);
      setItems(res.data.items || []);
      if (res.data.aggregations) setAggregations(res.data.aggregations);
      setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
    } catch (e) {
      toast.error('Failed to load re-sticker queue');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, sortBy, pagination.skip, pagination.limit, slug, sectionParam]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPagination((p) => ({ ...p, skip: 0 }));  // Reset to page 1 on new filter.
  };

  const fetchBatches = useCallback(async () => {
    try {
      const res = await apiClient.get(`/price-revision/batches${sectionParam}`);
      setBatches(res.data.batches || []);
    } catch (e) { /* silent */ }
  }, [sectionParam]);

  // 2026-05-18 — Bin Status report fetchers
  const fetchBinStatus = useCallback(async () => {
    setBinStatusLoading(true);
    try {
      const params = new URLSearchParams();
      if (sectionParam) {
        const sp = new URLSearchParams(sectionParam.startsWith('?') ? sectionParam.slice(1) : sectionParam);
        sp.forEach((v, k) => params.set(k, v));
      }
      if (binStatusFilters.bin_code.trim()) params.set('bin_code', binStatusFilters.bin_code.trim());
      if (binStatusFilters.hall.trim()) params.set('hall', binStatusFilters.hall.trim());
      if (binStatusFilters.row.trim()) params.set('row', binStatusFilters.row.trim());
      if (binStatusFilters.rack.trim()) params.set('rack', binStatusFilters.rack.trim());
      if (binStatusFilters.level.trim()) params.set('level', binStatusFilters.level.trim());
      if (binStatusFilters.has_unstickered_only) params.set('has_unstickered_only', 'true');
      params.set('limit', '500');
      const res = await apiClient.get(`/price-revision/bin-status-report?${params.toString()}`);
      setBinStatusRows(res.data.rows || []);
      setBinStatusTotals(res.data.totals || { bins: 0, total_packs: 0, total_unstickered: 0, total_clean: 0 });
      setBinStatusExpanded({});
      setBinStatusPacks({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bin status');
    } finally {
      setBinStatusLoading(false);
    }
  }, [sectionParam, binStatusFilters]);

  const toggleBinStatusRow = async (binCode, mode = 'all') => {
    const current = binStatusExpanded[binCode];
    if (current === mode) {
      // collapse
      const next = { ...binStatusExpanded };
      delete next[binCode];
      setBinStatusExpanded(next);
      return;
    }
    setBinStatusExpanded({ ...binStatusExpanded, [binCode]: mode });
    if (!binStatusPacks[binCode] || binStatusPacks[binCode]._mode !== mode) {
      setBinStatusPacks(p => ({ ...p, [binCode]: { loading: true, _mode: mode } }));
      try {
        const params = new URLSearchParams();
        if (sectionParam) {
          const sp = new URLSearchParams(sectionParam.startsWith('?') ? sectionParam.slice(1) : sectionParam);
          sp.forEach((v, k) => params.set(k, v));
        }
        params.set('bin_code', binCode);
        params.set('status_filter', mode);
        params.set('limit', '500');
        const res = await apiClient.get(`/price-revision/bin-status-report/packs?${params.toString()}`);
        setBinStatusPacks(p => ({
          ...p,
          [binCode]: { loading: false, packs: res.data.packs || [], _mode: mode },
        }));
      } catch (e) {
        setBinStatusPacks(p => ({ ...p, [binCode]: { loading: false, packs: [], _mode: mode } }));
        toast.error('Failed to load packs');
      }
    }
  };

  const exportBinStatusCsv = () => {
    if (binStatusRows.length === 0) { toast.error('Nothing to export'); return; }
    const header = ['Bin', 'Unstickered', 'Clean', 'Total', 'Last Put-Away', 'Sample SKUs'];
    const lines = [header.join(',')];
    binStatusRows.forEach(r => {
      lines.push([
        r.bin_code, r.unstickered, r.clean, r.total,
        r.last_put_away || '',
        (r.sample_skus || []).join(' | '),
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = `bin-status-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(u);
  };

  // Audit filter + pagination state.
  const [auditQuery, setAuditQuery] = useState({ pack_barcode: '', sku: '', operator: '' });
  const [auditPage, setAuditPage] = useState(1);
  const auditPageSize = 50;
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (sectionParam) {
        const sp = new URLSearchParams(sectionParam.startsWith('?') ? sectionParam.slice(1) : sectionParam);
        sp.forEach((v, k) => params.set(k, v));
      }
      if (auditQuery.pack_barcode.trim()) params.set('pack_barcode', auditQuery.pack_barcode.trim());
      if (auditQuery.sku.trim()) params.set('sku', auditQuery.sku.trim());
      if (auditQuery.operator.trim()) params.set('operator', auditQuery.operator.trim());
      params.set('skip', String((auditPage - 1) * auditPageSize));
      params.set('limit', String(auditPageSize));
      const res = await apiClient.get(`/price-revision/audit?${params}`);
      setAuditEvents(res.data.events || []);
      setAuditTotal(res.data.pagination?.total || 0);
    } catch (e) { /* silent */ } finally { setAuditLoading(false); }
  }, [sectionParam, auditQuery, auditPage]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);
  useEffect(() => { fetchBatches(); }, [fetchBatches]);
  useEffect(() => { if (activeTab === 'audit') fetchAudit(); }, [activeTab, fetchAudit]);

  // ─── Re-put-away report (2026-05-11) ──────────────────────────────────
  // Surfaces every wms_fg_inventory row stamped `put_away_from_cancellation`.
  const [repuSummary, setRepuSummary] = useState(null);
  const [repuRows, setRepuRows] = useState([]);
  const [repuStatus, setRepuStatus] = useState('STORED');
  const [repuSearch, setRepuSearch] = useState('');
  const [repuDateFrom, setRepuDateFrom] = useState('');
  const [repuDateTo, setRepuDateTo] = useState('');
  const [repuLoading, setRepuLoading] = useState(false);
  const [repuTruncated, setRepuTruncated] = useState(false);

  const fetchReputaway = useCallback(async () => {
    setRepuLoading(true);
    try {
      const params = new URLSearchParams();
      if (repuStatus && repuStatus !== 'ALL') params.set('status', repuStatus);
      if (repuSearch) params.set('search', repuSearch);
      if (repuDateFrom) params.set('date_from', repuDateFrom);
      if (repuDateTo) params.set('date_to', repuDateTo);
      params.set('limit', '1000');
      const { data } = await apiClient.get(
        `/price-revision/reputaway-report?${params}`,
      );
      setRepuRows(data.rows || []);
      setRepuSummary(data.summary || null);
      setRepuTruncated(!!data.truncated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-put-away report failed');
    } finally {
      setRepuLoading(false);
    }
  }, [repuStatus, repuSearch, repuDateFrom, repuDateTo]);

  useEffect(() => {
    if (activeTab === 'reputaway') fetchReputaway();
  }, [activeTab, fetchReputaway]);

  const handleReputawayCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (repuStatus && repuStatus !== 'ALL') params.set('status', repuStatus);
      if (repuSearch) params.set('search', repuSearch);
      if (repuDateFrom) params.set('date_from', repuDateFrom);
      if (repuDateTo) params.set('date_to', repuDateTo);
      const resp = await apiClient.get(
        `/price-revision/reputaway-report/export.csv?${params}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fg_reputaway_${repuStatus.toLowerCase()}_${repuDateFrom || 'all'}_to_${repuDateTo || 'all'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  // 2026-05-14 — Recover Stuck Packs dialog open + dry-run preview.
  const openStuckRecoveryDialog = async () => {
    setStuckDialogOpen(true);
    setStuckResult(null);
    setStuckPreview(null);
    setStuckSubmitting(true);
    try {
      const resp = await apiClient.post('/price-revision/recover-stuck-printed-packs', {
        section: filters.section || null,
        since_hours: stuckWindowHours,
        dry_run: true,
      });
      setStuckPreview(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview stuck packs');
      setStuckDialogOpen(false);
    } finally {
      setStuckSubmitting(false);
    }
  };

  // 2026-05-14 — Refresh dry-run preview when the window slider changes.
  const refreshStuckPreview = async () => {
    setStuckSubmitting(true);
    setStuckResult(null);
    try {
      const resp = await apiClient.post('/price-revision/recover-stuck-printed-packs', {
        section: filters.section || null,
        since_hours: stuckWindowHours,
        dry_run: true,
      });
      setStuckPreview(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setStuckSubmitting(false);
    }
  };

  const handleConfirmStuckRecovery = async () => {
    setStuckSubmitting(true);
    try {
      const resp = await apiClient.post('/price-revision/recover-stuck-printed-packs', {
        section: filters.section || null,
        since_hours: stuckWindowHours,
        staff_id: batchStaffId || null,
        staff_name: batchStaffName || null,
        dry_run: false,
      });
      setStuckResult(resp.data);
      if ((resp.data?.recovered || 0) > 0) {
        toast.success(resp.data.message);
        fetchQueue();
      } else {
        toast(resp.data?.message || 'Nothing recovered.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recovery failed');
    } finally {
      setStuckSubmitting(false);
    }
  };



  // 2026-05-14 — Same retry-on-demand pattern as FGPickSlips. The
  // on-mount fetch can lose to a 401 race or a network blip; we must
  // not silently swallow that and then block the operator at the
  // Print button with a misleading "retry in a moment" toast.
  const fetchLabelTemplates = useCallback(async () => {
    const res = await apiClient.get('/main-labels/templates');
    const list = res.data?.templates || [];
    setTemplates(list);
    return list;
  }, []);

  // One-time setup for USB-based ZPL printing (mirrors SFGMainLabelPrinting).
  useEffect(() => {
    setUsbSupported(printerApi.isWebUSBSupported());
    fetchLabelTemplates().catch((e) => {
      console.warn('ReStickerQueue: initial templates fetch failed (will retry on demand)', e);
    });
  }, [fetchLabelTemplates]);

  /**
   * Ensure label templates are available. Returns the current list (after
   * a retry-fetch if needed) or null if the fetch failed and the caller
   * should abort.
   */
  const ensureTemplatesLoaded = async () => {
    if (templates.length) return templates;
    const toastId = toast.loading('Loading label templates…');
    try {
      const list = await fetchLabelTemplates();
      toast.dismiss(toastId);
      if (!list.length) {
        toast.error('No label templates configured. Set up templates in Admin → Main Labels first.');
        return null;
      }
      return list;
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`Label templates failed to load: ${e?.response?.data?.detail || e?.message || e}`);
      return null;
    }
  };

  const handleConnectUSB = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected: ${device.productName || 'USB printer'}`);
    } catch (e) {
      toast.error(e.message || 'USB connect failed');
    }
  };
  const findTemplateByName = (name) => templates.find((t) => t.template_name === name) || templates[0];

  // ---- Admin actions ----
  const [refreshing, setRefreshing] = useState(false);
  const [reverting, setReverting] = useState(false);

  // ---- Reprint Stickers (printer-corruption recovery) ----
  const [reprintDialogOpen, setReprintDialogOpen] = useState(false);
  const [reprintPackInput, setReprintPackInput] = useState('');
  const [reprintLookups, setReprintLookups] = useState([]);  // [{pack_barcode, usn_codes, mrp, ...}]
  const [reprintSelectedUsns, setReprintSelectedUsns] = useState({});  // {pack_barcode: Set<usn>}
  const [reprintLoading, setReprintLoading] = useState(false);
  const [reprintProgress, setReprintProgress] = useState({ done: 0, total: 0, log: [] });

  const handleReprintLookup = async () => {
    const codes = reprintPackInput.split(/[\s,\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!codes.length) {
      toast.error('Paste or scan at least one pack barcode');
      return;
    }
    setReprintLoading(true);
    try {
      const res = await apiClient.post('/price-revision/reprint-lookup', {
        pack_barcodes: codes, section: slug || null,
      });
      setReprintLookups(res.data.packs || []);
      // Pre-tick all USNs by default so the operator only un-checks the good ones.
      const sel = {};
      (res.data.packs || []).forEach((p) => { sel[p.pack_barcode] = new Set(p.usn_codes); });
      setReprintSelectedUsns(sel);
      if ((res.data.missing || []).length) {
        toast.warning(`${res.data.missing.length} pack(s) not found: ${res.data.missing.join(', ')}`);
      } else {
        toast.success(`Loaded ${res.data.packs.length} pack(s) for reprint`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally {
      setReprintLoading(false);
    }
  };

  const toggleReprintUsn = (pb, usn) => {
    setReprintSelectedUsns((prev) => {
      const cur = new Set(prev[pb] || []);
      if (cur.has(usn)) cur.delete(usn); else cur.add(usn);
      return { ...prev, [pb]: cur };
    });
  };

  const handleReprintExecute = async () => {
    if (!usbDevice && !printerApi.isNetworkPrintActive()) {
      toast.error('Connect a USB printer first');
      return;
    }
    // 2026-05-14 — Retry-fetch if templates didn't load on mount.
    const tpls = await ensureTemplatesLoaded();
    if (!tpls) return;
    // Build the work list — only ticked USNs per pack.
    const work = reprintLookups
      .map((p) => ({ pack: p, usns: Array.from(reprintSelectedUsns[p.pack_barcode] || []) }))
      .filter((w) => w.usns.length);
    const totalUsns = work.reduce((a, w) => a + w.usns.length, 0);
    if (!totalUsns) {
      toast.error('Tick at least one USN to reprint');
      return;
    }
    setReprintLoading(true);
    setReprintProgress({ done: 0, total: totalUsns, log: [] });
    const printerConfig = PRINTER_CONFIGS[printerModel] || PRINTER_CONFIGS.TSC_TE210;
    try {
      // Resolve template label-data once per unique item barcode.
      const barcodes = Array.from(new Set(work.map((w) => w.pack.item_barcode).filter(Boolean)));
      let labelDataMap = {};
      let defaultTemplate = tpls[0];
      if (barcodes.length) {
        const batchRes = await apiClient.post('/main-labels/preview-batch', {
          barcodes, pricing_type: pricingType, template_id: tpls[0]?.template_id,
        });
        const noRate = noRateMessage(batchRes.data, pricingType);
        if (noRate) throw new Error(noRate);
        labelDataMap = batchRes.data.results || {};
        defaultTemplate = batchRes.data.template || defaultTemplate;
      }
      for (const w of work) {
        const cached = labelDataMap[w.pack.item_barcode];
        if (!cached) {
          setReprintProgress((p) => ({
            ...p, done: p.done + w.usns.length,
            log: [{ pack_barcode: w.pack.pack_barcode, status: 'failed', message: 'No label data', ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }
        const labelData = { ...(cached.label_data || {}), mrp: w.pack.mrp };
        const mrpFmt = `Rs.${Number(w.pack.mrp || 0).toFixed(2)}`;
        // IMPORTANT: match the main bulk-print's template resolution exactly.
        // Prefer the server-side print_template from preview-batch (canonical
        // item_master value); only fall back to the reprint-lookup hint or
        // default template if preview-batch didn't return one.
        const tplName = cached.print_template || w.pack.print_template || 'Standard';
        const template = findTemplateByName(tplName) || defaultTemplate;

        // 2026-05-11 — Corrective reprint also mints fresh REPLACE-pool USNs
        // so the new sticker has a different USN code than the corrupted one.
        // The deactivated-USN guard then blocks any pack still wearing the
        // corrupted sticker at put-away.
        let usnsToPrint = w.usns;
        try {
          const claimRes = await apiClient.post('/usn-replacement/replace-batch', {
            old_usns: w.usns,
            reason: `resticker_corrective_reprint:${w.pack.pack_barcode}`,
            source_flow: 'resticker_corrective_reprint',
            section: 'FG',
          });
          // 2026-05-12: backend returns `{ replacements: [...], failures: [...] }`
          // — earlier code read `data.results` (wrong field name) so the
          // mapping was always empty and EVERY successful claim looked
          // "missing", triggering a misleading "Top up the REPLACE pool"
          // error even when the pool was healthy and the new USNs were
          // already minted. Items in `replacements` carry `old_usn` +
          // `new_usn` with no `success` flag — presence in the array IS
          // the success signal.
          const mapping = (claimRes.data?.replacements || []).reduce((acc, r) => {
            if (r.new_usn) acc[r.old_usn] = r.new_usn;
            return acc;
          }, {});
          const failureDetail = (claimRes.data?.failures || [])
            .map((f) => `${f.old_usn}: ${f.detail || `HTTP ${f.status_code}`}`)
            .join('; ');
          const missing = w.usns.filter((u) => !mapping[u]);
          if (missing.length) {
            throw new Error(
              `Could not mint replacement USNs for ${missing.length} sticker(s)`
              + (failureDetail ? ` — ${failureDetail}` : '')
              + `. If the REPLACE pool is empty, top it up via Admin → USN Master Import (section=REPLACE).`,
            );
          }
          usnsToPrint = w.usns.map((u) => mapping[u]);
        } catch (claimErr) {
          const msg = claimErr.response?.data?.detail || claimErr.message || 'REPLACE-pool claim failed';
          setReprintProgress((p) => ({
            ...p, done: p.done + w.usns.length,
            log: [{ pack_barcode: w.pack.pack_barcode, status: 'failed', message: 'Replacement claim failed: ' + msg, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }

        let allCommands = '';
        for (const usn of usnsToPrint) {
          allCommands += generateTsplFromLabelData(
            labelData, template, usn, mrpFmt, null, printerConfig, template?.template_type,
          );
        }
        try {
          await printerApi.sendToUSBPrinter(usbDevice, allCommands);
          // Audit (no DB state change on the pack).
          await apiClient.post('/price-revision/reprint-audit', {
            pack_barcode: w.pack.pack_barcode,
            usn_codes: usnsToPrint,
            reason: 'printer_corruption',
            section: slug || null,
            staff_id: batchStaffId || null,
          });
          setReprintProgress((p) => ({
            ...p, done: p.done + usnsToPrint.length,
            log: [{ pack_barcode: w.pack.pack_barcode, status: 'printed', message: `${usnsToPrint.length} new USN(s)`, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
        } catch (e) {
          setReprintProgress((p) => ({
            ...p, done: p.done + usnsToPrint.length,
            log: [{ pack_barcode: w.pack.pack_barcode, status: 'failed', message: e.message || 'USB error', ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          if (e.message && /usb|printer|claim/i.test(e.message)) {
            toast.error('Printer error — stopping. ' + e.message);
            break;
          }
        }
      }
      toast.success('Reprint complete');
    } catch (e) {
      toast.error(e.message || 'Reprint failed');
    } finally {
      setReprintLoading(false);
    }
  };

  const handleRefreshStamps = async () => {
    if (!batches.length) {
      toast.error('No revision batches found — upload a CSV first');
      return;
    }
    // If only one batch, refresh that. Otherwise prompt the operator to pick.
    let revId = batches[0].revision_id;
    if (batches.length > 1) {
      const pick = window.prompt(
        `Which revision do you want to re-scan FG storage for? (paste the revision_id)\n\n` +
        batches.map((b) => `• ${b.revision_id}  —  ${b.revision_name} · ${b.effective_date}`).join('\n'),
        revId,
      );
      if (!pick) return;
      revId = pick.trim();
    }
    setRefreshing(true);
    try {
      const res = await apiClient.post('/price-revision/refresh-pack-stamps', {
        revision_id: revId, section: slug || null,
      });
      toast.success(`Re-scan complete: stamped ${res.data.newly_stamped} additional packs`);
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRevertDone = async () => {
    // 2026-05-12: open the bin-scoped revert dialog instead of immediately
    // reverting EVERYTHING. The operator prints stickers per-bin, so a
    // physical hiccup typically affects one bin — they should be able to
    // revert just that bin's USNs.
    setRevertBin('');
    setRevertPreview(null);
    setRevertDialogOpen(true);
  };

  const fetchRevertPreview = async (binCode) => {
    // Cheap preview — count packs in the chosen bin that would actually
    // be reverted. We re-use the queue endpoint with restickered=true +
    // last_stored_bin_code filter (case-insensitive on server side).
    const code = (binCode || '').trim();
    if (!code) {
      setRevertPreview({ count: 0 });
      return;
    }
    setRevertPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', 'done');
      params.set('bin_code', code);
      params.set('skip', '0');
      params.set('limit', '3');  // Get a few sample pack_barcodes too.
      const { data } = await apiClient.get(`/price-revision/pending-restickers?${params}`);
      setRevertPreview({
        count: (data?.aggregations?.done ?? data?.total ?? 0),
        sample: (data?.items || []).slice(0, 3).map((i) => i.pack_barcode),
      });
    } catch (e) {
      setRevertPreview({ count: null, error: e.response?.data?.detail || 'Preview failed' });
    } finally {
      setRevertPreviewLoading(false);
    }
  };

  const confirmBinScopedRevert = async () => {
    const bin = (revertBin || '').trim();
    if (!bin) {
      toast.error('Enter the Bin Number first');
      return;
    }
    setReverting(true);
    try {
      const res = await apiClient.post('/price-revision/bulk-revert-restickered', {
        section: slug || null,
        bin_code: bin,
      });
      const n = res.data?.reverted || 0;
      const stuck = res.data?.recovered_stuck || 0;
      if (n === 0) {
        toast.info(`No Done packs found in bin ${bin}`);
      } else {
        toast.success(
          `Reverted ${n} pack(s) from bin ${bin}`
          + (stuck ? ` (incl. ${stuck} recovered from stuck PENDING_PUT_AWAY)` : ''),
        );
      }
      setRevertDialogOpen(false);
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    } finally {
      setReverting(false);
    }
  };

  const handleValidateStaff = async () => {
    if (!batchStaffId) {
      toast.error('Enter a Staff ID');
      return;
    }
    setStaffValidating(true);
    try {
      const res = await apiClient.post('/validate/staff', { staff_id: batchStaffId });
      if (!res.data || res.data.valid === false) {
        toast.error('Invalid Staff ID');
        setBatchStaffName('');
        return;
      }
      setBatchStaffName(res.data.staff_name || res.data.name || batchStaffId);
      toast.success(`Staff verified: ${res.data.staff_name || batchStaffId}`);
      setStaffPromptOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Staff validation failed');
    } finally {
      setStaffValidating(false);
    }
  };

  const allOnPageSelected = items.length > 0 && items.every((i) => selected.has(i.pack_barcode));
  const toggleAllOnPage = () => {
    const next = new Set(selected);
    if (allOnPageSelected) {
      items.forEach((i) => next.delete(i.pack_barcode));
    } else {
      items.forEach((i) => i.pack_barcode && next.add(i.pack_barcode));
    }
    setSelected(next);
  };

  const togglePack = (barcode) => {
    const next = new Set(selected);
    if (next.has(barcode)) next.delete(barcode);
    else next.add(barcode);
    setSelected(next);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName || !uploadDate) {
      toast.error('File, name, and effective date are required');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      const qp = new URLSearchParams({
        revision_name: uploadName,
        effective_date: uploadDate,
        ...(slug ? { section: slug } : {}),
      });
      const res = await apiClient.post(`/price-revision/upload-csv?${qp}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${res.data.item_count} SKUs · stamped ${res.data.affected_packs} FG packs`);
      setUploadOpen(false);
      setUploadFile(null);
      setUploadName('');
      fetchQueue();
      fetchBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleStartBatchPrint = async () => {
    const barcodes = Array.from(selected).slice(0, MAX_BATCH);
    if (barcodes.length === 0) return;
    if (!usbDevice && !printerApi.isNetworkPrintActive()) {
      toast.error('Connect a USB printer first');
      return;
    }
    // 2026-05-14 — Retry-fetch if templates didn't load on mount.
    const tpls = await ensureTemplatesLoaded();
    if (!tpls) return;

    setPrinting(true);
    setPrintProgress({ done: 0, total: barcodes.length, success: 0, failed: 0, log: [] });

    // Abort flag is read inside the loop after each USB send so the current
    // pack always finishes cleanly (avoids a half-printed label).
    let aborted = false;
    setPrintAbortFn(() => () => { aborted = true; });

    const printerConfig = PRINTER_CONFIGS[printerModel] || PRINTER_CONFIGS.TSC_TE210;

    try {
      // Build a barcode→pack map from the in-memory rows so we don't have to
      // re-query per pack. Any selected pack that isn't in the current page
      // is fetched individually via the queue (limit=1).
      const packMap = new Map(items.map((p) => [p.pack_barcode, p]));
      const missingPacks = barcodes.filter((b) => !packMap.has(b));
      if (missingPacks.length) {
        // Fetch missing pack details in one query.
        const params = new URLSearchParams(sectionParam ? { section: slug } : {});
        params.append('limit', String(missingPacks.length));
        params.append('pack_barcodes', missingPacks.join(','));
        try {
          const r = await apiClient.get(`/price-revision/pending-restickers?${params}`);
          (r.data.items || []).forEach((p) => packMap.set(p.pack_barcode, p));
        } catch { /* fall through — those packs will fail with a clear reason */ }
      }

      // Resolve label data in ONE batch via existing /main-labels/preview-batch.
      const itemBarcodes = Array.from(new Set(
        barcodes.map((b) => packMap.get(b)?.item_barcode || packMap.get(b)?.sku_code).filter(Boolean)
      ));
      const labelDataMap = {};
      let defaultTemplate = tpls[0];
      let noRate = null;
      if (itemBarcodes.length) {
        try {
          const batchRes = await apiClient.post('/main-labels/preview-batch', {
            barcodes: itemBarcodes,
            pricing_type: pricingType,
            template_id: tpls[0]?.template_id,
          });
          Object.assign(labelDataMap, batchRes.data.results || {});
          defaultTemplate = batchRes.data.template || defaultTemplate;
          noRate = noRateMessage(batchRes.data, pricingType);
        } catch (e) {
          throw new Error('Failed to load label data: ' + (e.response?.data?.detail || e.message));
        }
        if (noRate) throw new Error(noRate);
      }

      // ============================================================
      // 2026-05-12: CONTINUOUS-PRINT REWRITE
      // ------------------------------------------------------------
      // Previously this loop ran serially per-pack:
      //   for each pack: claim → TSPL → send → mark
      // Each pack paid 3-15s of round-trip latency for the claim alone,
      // and the printer sat idle between packs. For 100 packs at 5s/pack
      // that's ~8 min of mostly-waiting wall-clock.
      //
      // New flow — true batch:
      //   1. PHASE 1: ONE `/replace-batch` call with ALL old USNs across
      //      the whole selection → backend fans out at max concurrency.
      //   2. PHASE 2: build a single TSPL blob across every pack.
      //   3. PHASE 3: stream the entire blob to the USB printer in ONE
      //      shot → printer prints continuously, no per-pack pauses.
      //   4. PHASE 4: ONE `/mark-restickered-bulk` call flips every pack
      //      to PENDING_PUT_AWAY in parallel server-side.
      //
      // Audit log captures every (old_usn → new_usn) mapping at the
      // moment of mint, so if the printer chokes mid-blob the operator
      // can resume from the audit log with the new USNs already known.
      // ============================================================
      // ---- PHASE 0: prepare per-pack TSPL plan, skip already-done ----
      const plans = [];  // [{ pb, pack, template, boxTpl, labelData, newMrpFmt, isCombo, claimUsns }]
      for (let i = 0; i < barcodes.length; i++) {
        if (aborted) break;
        const pb = barcodes[i];
        const pack = packMap.get(pb);
        if (!pack) {
          setPrintProgress((p) => ({
            ...p, done: p.done + 1, failed: p.failed + 1,
            log: [{ pack_barcode: pb, status: 'failed', message: 'Pack details not found', ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }
        if (pack.restickered_at) {
          setPrintProgress((p) => ({
            ...p, done: p.done + 1, success: p.success + 1,
            log: [{ pack_barcode: pb, status: 'skipped', message: `${pb} already restickered`, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }
        const effectiveBarcode = pack.item_barcode || pack.sku_code;
        const cached = labelDataMap[effectiveBarcode];
        if (!cached) {
          setPrintProgress((p) => ({
            ...p, done: p.done + 1, failed: p.failed + 1,
            log: [{ pack_barcode: pb, status: 'failed', message: `No label data for barcode ${effectiveBarcode}`, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }
        // 2026-05-14 — MRP is sourced from Item Master / Rates Master
        // (via /main-labels/preview-batch → cached.label_data.mrp), NOT
        // from the CSV-uploaded `to_mrp` on the pack. The CSV is the
        // trigger for *which* packs need re-stickering; the MRP value
        // to print is the canonical master value at print-time. Falls
        // back to pack.to_mrp only when preview-batch returned no rates
        // row (legacy data).
        const masterMrp = cached.label_data?.mrp || pack.to_mrp || 0;
        const labelData = { ...(cached.label_data || {}), mrp: masterMrp };
        const newMrpFmt = `Rs.${Number(masterMrp).toFixed(2)}`;
        const tplName = cached.print_template || 'Standard';
        const template = findTemplateByName(tplName) || defaultTemplate;
        const oldUsnList = pack.usn_codes && pack.usn_codes.length ? pack.usn_codes : [pack.pack_barcode];
        // 2026-05-13 — Box-sticker eligibility now depends ONLY on
        // `box_template` being set + not "0". The previous gate also
        // required `template_type` to contain "combo", but many items
        // configured with a real box_template value have their main
        // print_template's `template_type` as "Standard" (not "combo"),
        // so the box sticker was being silently skipped. User report:
        // "FG Re-Sticker Queue does not print the box stickers for items
        // which has the box template with a value."
        const isCombo = !!(cached.box_template && cached.box_template !== '0');
        const claimUsns = isCombo ? [oldUsnList[0]] : oldUsnList;
        const boxTpl = isCombo ? (findTemplateByName(cached.box_template) || defaultTemplate) : null;
        plans.push({ pb, pack, template, boxTpl, labelData, newMrpFmt, isCombo, claimUsns });
      }

      if (!plans.length) {
        if (!aborted) toast.info('Nothing to print — all selected packs were already restickered or had errors');
        return;
      }

      // ---- PHASE 1: stream the replace claim via SSE ----
      // 2026-05-12 (late night) — production was hitting Cloudflare 524
      // (origin timeout, ~100s) when a single non-streaming `/replace-batch`
      // POST processed 75+ packs (~500 USNs). Switched to the streaming
      // variant: SSE keeps the connection alive (heart-beats every per-USN
      // event), Cloudflare never times out, AND we get real-time progress
      // we can surface in the dialog.
      setPrintProgress((p) => ({
        ...p,
        log: [{ pack_barcode: '*', status: 'minting', message: `Minting replacement USNs for ${plans.length} pack(s)…`, ts: Date.now() }, ...p.log].slice(0, 50),
      }));
      const allOldUsns = plans.flatMap((pl) => pl.claimUsns);
      let mapping = {};
      let claimFailures = [];
      try {
        const streamRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/usn-replacement/replace-batch-stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('session_token') || ''}`,
              'X-Site-Id': localStorage.getItem('active_site_id') || 'salem',
            },
            body: JSON.stringify({
              old_usns: allOldUsns,
              reason: `resticker_continuous_print:${plans.length}_packs`,
              source_flow: 'resticker_reprint',
              section: 'FG',
            }),
          },
        );
        if (!streamRes.ok) {
          const errText = await streamRes.text();
          throw new Error(`HTTP ${streamRes.status}: ${errText.slice(0, 300)}`);
        }
        const reader = streamRes.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let mintedCount = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const rawEvent = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = rawEvent.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let evt;
            try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
            if (evt.type === 'progress') {
              if (evt.ok && evt.new_usn) {
                mapping[evt.old_usn] = evt.new_usn;
                mintedCount++;
              } else if (!evt.ok) {
                claimFailures.push({
                  old_usn: evt.old_usn,
                  detail: evt.detail,
                  status_code: evt.status_code,
                });
              }
              // Surface live progress on the dialog log.
              setPrintProgress((p) => ({
                ...p,
                log: [{
                  pack_barcode: '*', status: 'minting',
                  message: `Minted ${mintedCount}/${allOldUsns.length} USNs…`,
                  ts: Date.now(),
                }, ...p.log.filter((l) => l.status !== 'minting' || l.pack_barcode !== '*').slice(0, 49)],
              }));
            } else if (evt.type === 'complete') {
              // Final reconciliation — server gives the authoritative list.
              (evt.replacements || []).forEach((r) => {
                if (r.new_usn) mapping[r.old_usn] = r.new_usn;
              });
              claimFailures = evt.failures || claimFailures;
            } else if (evt.type === 'error') {
              throw new Error(evt.detail || 'Stream error');
            }
          }
        }
      } catch (claimErr) {
        const msg = claimErr.message || 'REPLACE-pool claim failed';
        toast.error('Replacement claim failed: ' + msg);
        return;
      }

      // Report per-pack claim failures BEFORE printing so the operator sees
      // exactly which packs got skipped and why.
      const failureMap = claimFailures.reduce((acc, f) => {
        acc[f.old_usn] = f.detail || `HTTP ${f.status_code}`;
        return acc;
      }, {});
      const printablePlans = [];
      for (const pl of plans) {
        const missing = pl.claimUsns.filter((u) => !mapping[u]);
        if (missing.length) {
          const detail = missing.map((u) => `${u}: ${failureMap[u] || 'no replacement'}`).join('; ');
          setPrintProgress((p) => ({
            ...p, done: p.done + 1, failed: p.failed + 1,
            log: [{ pack_barcode: pl.pb, status: 'failed', message: `Mint failed — ${detail}`, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
          continue;
        }
        pl.newUsns = pl.claimUsns.map((u) => mapping[u]);
        printablePlans.push(pl);
      }
      if (!printablePlans.length) {
        // 2026-05-15 — Aggregate the top failure reasons so the operator
        // doesn't have to dig through the dialog log. When the REPLACE
        // pool is empty (the most common cause after heavy re-sticker
        // batches), surface the import-path hint inline.
        const reasonCounts = {};
        for (const f of claimFailures) {
          const key = (f.detail || `HTTP ${f.status_code}`).slice(0, 140);
          reasonCounts[key] = (reasonCounts[key] || 0) + 1;
        }
        const topReasons = Object.entries(reasonCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([k, v]) => `• ${v}×  ${k}`)
          .join('\n');
        const isPoolEmpty = Object.keys(reasonCounts).some(
          (r) => /REPLACE.*pool|Insufficient USNs/i.test(r),
        );
        toast.error(
          isPoolEmpty
            ? `No packs were minted — REPLACE pool is empty.\n\n${topReasons}\n\nFix: Admin → USN Master Import (section="REPLACE").`
            : `No packs were minted — top failure reasons:\n\n${topReasons || 'See the dialog log.'}`,
          { duration: 14000, data: { testid: 'mint-all-failed-toast' } },
        );
        return;
      }

      // ---- PHASE 1B: PRE-PRINT PROMPT ----
      // 2026-05-14 (C) — Operator MUST confirm the product printer AND
      // can override the auto-resolved product template before any
      // TSPL hits the wire. Alignment issues normally come from a
      // template/roll-size mismatch — surfacing the picker here lets
      // the operator correct it before the batch goes out.
      const templateModeOf = (arr, key) => {
        const counts = {};
        for (const x of arr) {
          const name = x[key]?.template_name;
          if (name) counts[name] = (counts[name] || 0) + 1;
        }
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
      };
      const initialComboCount = printablePlans.filter((p) => p.isCombo).length;
      const suggestedProductTemplate = templateModeOf(printablePlans, 'template');
      const suggestedBoxTemplate = initialComboCount > 0
        ? templateModeOf(printablePlans.filter((p) => p.isCombo), 'boxTpl')
        : null;
      const availableTemplateNames = templates.map((t) => t.template_name).filter(Boolean);

      const startResp = await new Promise((resolve) => {
        setPhaseAPackCount(printablePlans.length);
        setPhaseAComboCount(initialComboCount);
        setPhaseAProductTemplate(suggestedProductTemplate || '');
        setPhaseAAvailableTemplates(availableTemplateNames);
        setPhaseADevice(usbDevice || null);
        setPhaseAResolve(() => resolve);
        setPhaseAOpen(true);
      });
      if (startResp && startResp.cancelled) {
        toast.info('Print cancelled before product phase. Packs NOT marked.');
        return;
      }
      let activeDevice = (startResp && startResp.device) || usbDevice;
      if (startResp && startResp.productTemplate) {
        const tpl = findTemplateByName(startResp.productTemplate);
        if (tpl) for (const pl of printablePlans) pl.template = tpl;
      }
      // Stash the suggested box template for Phase B (default value).
      const phaseBSuggestedTpl = suggestedBoxTemplate;

      // ---- PHASE 2: build TWO TSPL blobs (product vs box) for two-phase print ----
      // 2026-05-14 — User spec: when combos are in the batch, print all
      // product (inner) stickers first, prompt the operator to load the
      // box-label roll, then print the box stickers. Non-combo single-USN
      // packs always print with the product (inner) phase. The dialog
      // only opens when comboCount > 0.
      // Re combo USN-display: `generateTsplFromLabelData(labelData, template,
      // usnCode, mrpFormatted, displayUsn, printerConfig, templateType,
      // pairNumber)`. Pass `null` as displayUsn (defaults to usnCode) and
      // `pairNum` at the 8th positional arg.
      let productBlob = '';
      let boxBlob = '';
      let comboCount = 0;
      for (const pl of printablePlans) {
        if (pl.isCombo) {
          const sharedUsn = pl.newUsns[0];
          const pairNum = pl.pack.pair_number || 1;
          productBlob += generateTsplFromLabelData(
            pl.labelData, pl.template, sharedUsn, pl.newMrpFmt,
            null, printerConfig, pl.template?.template_type, pairNum,
          );
          boxBlob += generateTsplFromLabelData(
            pl.labelData, pl.boxTpl, sharedUsn, pl.newMrpFmt,
            null, printerConfig, pl.boxTpl?.template_type, pairNum,
          );
          comboCount += 1;
        } else {
          for (const usn of pl.newUsns) {
            productBlob += generateTsplFromLabelData(
              pl.labelData, pl.template, usn, pl.newMrpFmt, null,
              printerConfig, pl.template?.template_type,
            );
          }
        }
      }
      if (!productBlob && !boxBlob) {
        toast.error('No print commands generated');
        return;
      }

      // ---- PHASE 3A: PRODUCT STICKERS (inner + non-combo) ----
      if (productBlob) {
        setPrintProgress((p) => ({
          ...p,
          log: [{ pack_barcode: '*', status: 'printing', message: `Streaming product stickers for ${printablePlans.length} pack(s)…`, ts: Date.now() }, ...p.log].slice(0, 50),
        }));
        try {
          await printerApi.sendToUSBPrinter(activeDevice, productBlob);
        } catch (printErr) {
          // The new USNs are ALREADY minted in DB and the audit log has every
          // mapping. Operator can search the audit log and resume from there.
          toast.error(
            `Printer error mid-batch (product phase) — new USNs are minted and recorded in the Audit Log. `
            + `Search audit log for source_flow=resticker_reprint to resume. `
            + (printErr.message || ''),
            { duration: 12000 },
          );
          // Don't mark anything as restickered — physical stickers didn't land.
          return;
        }
      }

      // ---- PHASE 3B: BOX STICKERS (combo only) ----
      // 2026-05-14 — Two-phase prompt only fires when the batch contains
      // combos. Operator MUST connect the box-label printer AND pick a
      // box-sticker template in the prompt. Continue streams the box
      // blob (re-rendered against the chosen template) to THAT device.
      // Cancel skips box stickers BUT still marks every pack as
      // restickered (per user spec — admin will Recover/Re-arm if needed).
      if (boxBlob && comboCount > 0) {
        setPrintProgress((p) => ({
          ...p,
          log: [{ pack_barcode: '*', status: 'paused', message: `Product stickers done. Waiting for operator to connect box-label printer for ${comboCount} combo pack(s)…`, ts: Date.now() }, ...p.log].slice(0, 50),
        }));
        const transition = await new Promise((resolve) => {
          setPhaseDialogComboCount(comboCount);
          setPhaseBoxTemplate(phaseBSuggestedTpl || '');
          setPhaseBoxAvailableTemplates(availableTemplateNames);
          setPhaseDialogResolve(() => resolve);
          setPhaseDialogOpen(true);
        });
        if (transition && transition.cancelled) {
          setPrintProgress((p) => ({
            ...p,
            log: [{ pack_barcode: '*', status: 'skipped', message: `Box-sticker phase cancelled — ${comboCount} combo box sticker(s) NOT printed. Packs will still be marked.`, ts: Date.now() }, ...p.log].slice(0, 50),
          }));
        } else {
          const boxDevice = (transition && transition.device) || activeDevice;
          // 2026-05-14 (C) — Re-render the box blob if operator picked a
          // different template than the auto-resolved one.
          if (transition && transition.boxTemplate) {
            const newBoxTpl = findTemplateByName(transition.boxTemplate);
            if (newBoxTpl) {
              boxBlob = '';
              for (const pl of printablePlans) {
                if (!pl.isCombo) continue;
                const sharedUsn = pl.newUsns[0];
                const pairNum = pl.pack.pair_number || 1;
                boxBlob += generateTsplFromLabelData(
                  pl.labelData, newBoxTpl, sharedUsn, pl.newMrpFmt,
                  null, printerConfig, newBoxTpl?.template_type, pairNum,
                );
              }
            }
          }
          try {
            await printerApi.sendToUSBPrinter(boxDevice, boxBlob);
            setPrintProgress((p) => ({
              ...p,
              log: [{ pack_barcode: '*', status: 'printed', message: `Box stickers printed for ${comboCount} combo pack(s) on ${boxDevice.productName || 'USB printer'}`, ts: Date.now() }, ...p.log].slice(0, 50),
            }));
          } catch (boxErr) {
            // Per spec: even on box-print error we still mark packs as restickered.
            toast.error(`Box-sticker print error: ${boxErr.message || boxErr}. Packs will still be marked.`, { duration: 10000 });
            setPrintProgress((p) => ({
              ...p,
              log: [{ pack_barcode: '*', status: 'failed', message: `Box-sticker USB error: ${boxErr.message || boxErr}`, ts: Date.now() }, ...p.log].slice(0, 50),
            }));
          }
        }
      }

      // ---- PHASE 4: ONE bulk mark-restickered for all printed packs ----
      try {
        await apiClient.post('/price-revision/mark-restickered-bulk', {
          packs: printablePlans.map((pl) => ({
            pack_barcode: pl.pb,
            usn_codes: pl.newUsns,
          })),
          section: slug || null,
          staff_id: batchStaffId || null,
          staff_name: batchStaffName || null,
        });
      } catch (markErr) {
        // 2026-05-14 — More helpful 520 messaging. Direct admin to the
        // Recover Stuck Packs flow on the "Failed (needs retry)" tile
        // instead of leaving them guessing about manual cleanup.
        const status = markErr?.response?.status;
        const isPartial = status === 520 || status === 524 || status === 502;
        toast.error(
          isPartial
            ? (
              `Print succeeded but mark-restickered ${status}'d mid-batch. `
              + `The packs are physically printed but the DB didn't flip. `
              + `Click "Recover Stuck Packs" on the red Failed tile to auto-fix.`
            )
            : (
              `Print succeeded but mark-restickered failed: ${markErr.response?.data?.detail || markErr.message}. `
              + `Refresh the queue and re-run if necessary.`
            ),
          { duration: 15000 },
        );
      }

      // Update progress in one shot — every printable pack succeeded.
      setPrintProgress((p) => ({
        ...p,
        done: p.done + printablePlans.length,
        success: p.success + printablePlans.length,
        log: [
          {
            pack_barcode: '*',
            status: 'printed',
            message: `Printed ${printablePlans.length} pack(s) in one continuous batch`,
            ts: Date.now(),
          },
          ...p.log,
        ].slice(0, 50),
      }));

      if (!aborted) {
        toast.success('Batch print complete');
        // Auto-close so the operator doesn't see the same "Confirm Bulk Print"
        // dialog popping back into focus after stickering completes.
        setPrintDialogOpen(false);
        // Clear selection so a stray Print Batch click doesn't re-fire on
        // packs that were just stickered.
        setSelected(new Set());
      }
    } catch (e) {
      toast.error(e.message || 'Print failed');
    } finally {
      setPrinting(false);
      setPrintAbortFn(null);
      fetchQueue();
    }
  };

  const handleAbort = () => {
    if (printAbortFn) {
      printAbortFn();
      toast.info('Print batch aborted — already-printed packs stay marked Done');
    }
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPagination((p) => ({ ...p, skip: 0 }));
  };

  const filterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== '' && v !== 'pending').length,
    [filters],
  );

  if (slug && !sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="re-sticker-queue">
      <header className="bg-[#1A1F2E] text-white shadow">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(backRoute)} className="text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Printer className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">FG Re-Sticker Queue</h1>
              <p className="text-xs text-gray-300">{sectionName} · Bulk re-sticker after price revision</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setReprintDialogOpen(true)} variant="secondary" data-testid="reprint-stickers-btn" title="Reprint stickers for already-Done packs (printer corruption recovery)" className="bg-orange-100 hover:bg-orange-200 text-orange-900">
              <Printer className="w-4 h-4 mr-2" /> Reprint Stickers
            </Button>
            {canRunAdminActions && (
              <>
                <Button onClick={handleRefreshStamps} variant="secondary" disabled={refreshing} data-testid="refresh-stamps-btn" title="Re-scan FG storage to catch new put-aways since the original upload">
                  {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Refresh stamps
                </Button>
                <Button onClick={handleRevertDone} variant="secondary" disabled={reverting} data-testid="revert-done-btn" title="Revert wrongly-marked-Done packs (physical print didn't happen)" className="bg-amber-100 hover:bg-amber-200 text-amber-900">
                  {reverting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Revert Done
                </Button>
                <Button onClick={() => setUploadOpen(true)} variant="secondary" data-testid="upload-csv-btn">
                  <Upload className="w-4 h-4 mr-2" /> Upload Price Revision CSV
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4 space-y-4">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-amber-50 border-amber-200" data-testid="tile-pending">
            <CardContent className="p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-xs text-amber-700">Pending</p>
                  <p className="text-3xl font-bold text-amber-900">{aggregations.pending}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200" data-testid="tile-done">
            <CardContent className="p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-xs text-green-700">Done</p>
                  <p className="text-3xl font-bold text-green-900">{aggregations.done}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200" data-testid="tile-failed">
            <CardContent className="p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-xs text-red-700">Failed (needs retry)</p>
                  <p className="text-3xl font-bold text-red-900">{aggregations.failed}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              {/* 2026-05-14 — Admin escape hatch for HTTP-520 partial bulks.
                  Detects packs whose new USN was minted + physically printed
                  but `restickered_at` was never written (mark step 520'd).
                  Click → dry-run preview → admin confirms → bulk recovery. */}
              {(typeof window !== 'undefined' && (localStorage.getItem('role') || '').toLowerCase() === 'admin') && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full text-xs border-red-300 text-red-700 hover:bg-red-100"
                  onClick={openStuckRecoveryDialog}
                  data-testid="recover-stuck-packs-btn"
                  title="Recover packs that physically printed but didn't get marked"
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1" />
                  Recover Stuck Packs
                </Button>
              )}
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200" data-testid="tile-filtered">
            <CardContent className="p-4">
              <div className="flex justify-between">
                <div>
                  <p className="text-xs text-blue-700">In current filter</p>
                  <p className="text-3xl font-bold text-blue-900">{aggregations.filtered}</p>
                </div>
                <Filter className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="queue" data-testid="tab-queue"><Box className="w-4 h-4 mr-2" /> Queue</TabsTrigger>
            <TabsTrigger value="batches" data-testid="tab-batches"><FileText className="w-4 h-4 mr-2" /> Revision Batches</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><CheckCircle className="w-4 h-4 mr-2" /> Audit</TabsTrigger>
            <TabsTrigger value="reputaway" data-testid="tab-reputaway"><PackageOpen className="w-4 h-4 mr-2" /> Re-Put-Away</TabsTrigger>
            <TabsTrigger value="binstatus" data-testid="tab-binstatus"><Box className="w-4 h-4 mr-2" /> Bin Status</TabsTrigger>
          </TabsList>

          <TabsContent value="queue" className="space-y-4">
            {/* Filter card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Filters</CardTitle>
                  {filterCount > 0 && <Badge variant="secondary">{filterCount} active</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters">
                    {showFilters ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    {showFilters ? 'Hide' : 'Show'}
                  </Button>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </CardHeader>
              {showFilters && (
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Location</p>
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                      <Input placeholder="Hall" value={filters.hall} onChange={(e) => setFilters({ ...filters, hall: e.target.value })} data-testid="filter-hall" />
                      <Input placeholder="Row" value={filters.row} onChange={(e) => setFilters({ ...filters, row: e.target.value })} data-testid="filter-row" />
                      <Input placeholder="Rack" value={filters.rack} onChange={(e) => setFilters({ ...filters, rack: e.target.value })} data-testid="filter-rack" />
                      <Input placeholder="Level" value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })} data-testid="filter-level" />
                      <Input placeholder="Bin from" value={filters.bin_from} onChange={(e) => setFilters({ ...filters, bin_from: e.target.value })} data-testid="filter-bin-from" />
                      <Input placeholder="Bin to" value={filters.bin_to} onChange={(e) => setFilters({ ...filters, bin_to: e.target.value })} data-testid="filter-bin-to" />
                      <Select value={filters.velocity_class || 'all'} onValueChange={(v) => setFilters({ ...filters, velocity_class: v === 'all' ? '' : v })}>
                        <SelectTrigger data-testid="filter-velocity"><SelectValue placeholder="Velocity" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All zones</SelectItem>
                          <SelectItem value="A">A (fast)</SelectItem>
                          <SelectItem value="B">B</SelectItem>
                          <SelectItem value="C">C (slow)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Product</p>
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                      <Input placeholder="SKU" value={filters.sku} onChange={(e) => setFilters({ ...filters, sku: e.target.value })} data-testid="filter-sku" />
                      <Input placeholder="Design name" value={filters.design_name} onChange={(e) => setFilters({ ...filters, design_name: e.target.value })} data-testid="filter-design-name" />
                      <Input placeholder="Design code" value={filters.design_code} onChange={(e) => setFilters({ ...filters, design_code: e.target.value })} data-testid="filter-design-code" />
                      <Input placeholder="Color" value={filters.color} onChange={(e) => setFilters({ ...filters, color: e.target.value })} data-testid="filter-color" />
                      <Input placeholder="Size" value={filters.size} onChange={(e) => setFilters({ ...filters, size: e.target.value })} data-testid="filter-size" />
                      <Input placeholder="Category4" value={filters.category4} onChange={(e) => setFilters({ ...filters, category4: e.target.value })} data-testid="filter-category4" />
                      <Input placeholder="Brand" value={filters.brand} onChange={(e) => setFilters({ ...filters, brand: e.target.value })} data-testid="filter-brand" />
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Revision</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <Select value={filters.revision_id || 'all'} onValueChange={(v) => setFilters({ ...filters, revision_id: v === 'all' ? '' : v })}>
                        <SelectTrigger data-testid="filter-revision"><SelectValue placeholder="Revision batch" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All batches</SelectItem>
                          {batches.map((b) => (
                            <SelectItem key={b.revision_id} value={b.revision_id}>{b.revision_name} · {b.effective_date}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input placeholder="% change ≥" type="number" value={filters.pct_change_min} onChange={(e) => setFilters({ ...filters, pct_change_min: e.target.value })} data-testid="filter-pct-min" />
                      <Input placeholder="% change ≤" type="number" value={filters.pct_change_max} onChange={(e) => setFilters({ ...filters, pct_change_max: e.target.value })} data-testid="filter-pct-max" />
                      <Input placeholder="Abs Δ ≥ ₹" type="number" value={filters.abs_change_min} onChange={(e) => setFilters({ ...filters, abs_change_min: e.target.value })} data-testid="filter-abs-min" />
                      <Select value={filters.direction || 'both'} onValueChange={(v) => setFilters({ ...filters, direction: v === 'both' ? '' : v })}>
                        <SelectTrigger data-testid="filter-direction"><SelectValue placeholder="Direction" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="both">Both</SelectItem>
                          <SelectItem value="increase">Increase only</SelectItem>
                          <SelectItem value="decrease">Decrease only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Status &amp; Order</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                        <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="date" placeholder="Put-away from" value={filters.put_away_from} onChange={(e) => setFilters({ ...filters, put_away_from: e.target.value })} data-testid="filter-pa-from" />
                      <Input type="date" placeholder="Put-away to" value={filters.put_away_to} onChange={(e) => setFilters({ ...filters, put_away_to: e.target.value })} data-testid="filter-pa-to" />
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bin_sku">Bin then SKU (walk order)</SelectItem>
                          <SelectItem value="bin">Bin only</SelectItem>
                          <SelectItem value="sku">SKU only</SelectItem>
                          <SelectItem value="put_away">Put-away date (FIFO)</SelectItem>
                          <SelectItem value="delta_pct">% Δ (largest first)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={applyFilters} disabled={loading} data-testid="apply-filters-btn">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Apply
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Bulk action bar */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant="default" className="bg-blue-600" data-testid="selection-count">{selected.size} selected</Badge>
                  {selected.size > MAX_BATCH && (
                    <span className="text-xs text-red-600">Only first {MAX_BATCH} will print per batch</span>
                  )}
                  {/* USB printer status — required before Print Batch can fire. */}
                  {usbSupported ? (
                    usbDevice ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300" data-testid="usb-connected-badge">
                        <Usb className="w-3 h-3 mr-1" /> {usbDevice.productName || 'USB printer'}
                      </Badge>
                    ) : (
                      <Button variant="outline" size="sm" onClick={handleConnectUSB} data-testid="connect-usb-btn">
                        <Usb className="w-4 h-4 mr-2" /> Connect USB printer
                      </Button>
                    )
                  ) : (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                      <AlertTriangle className="w-3 h-3 mr-1" /> WebUSB not supported
                    </Badge>
                  )}
                  <Select value={printerModel} onValueChange={setPrinterModel}>
                    <SelectTrigger className="h-8 w-44" data-testid="printer-model-select">
                      <SelectValue placeholder="Printer model" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRINTER_CONFIGS).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={pricingType} onValueChange={setPricingType}>
                    <SelectTrigger className="h-8 w-24" data-testid="pricing-type-select">
                      <SelectValue placeholder="Pricing" />
                    </SelectTrigger>
                    <SelectContent>
                      {pricingChannels.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 items-center">
                  {/* Staff badge — required before Print Batch can fire. */}
                  {batchStaffId ? (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300" data-testid="batch-staff-badge">
                      Staff: {batchStaffName || batchStaffId}
                      <button onClick={() => { setBatchStaffId(''); setBatchStaffName(''); }} className="ml-2 text-xs underline">change</button>
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setStaffPromptOpen(true)} data-testid="capture-staff-btn">
                      Scan Staff ID
                    </Button>
                  )}
                  <Button variant="outline" size="sm" disabled={selected.size === 0} onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
                    Clear selection
                  </Button>
                  <Button disabled={selected.size === 0 || !usbDevice || !batchStaffId} onClick={() => setPrintDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="print-batch-btn">
                    <Printer className="w-4 h-4 mr-2" /> Print Batch ({Math.min(selected.size, MAX_BATCH)})
                  </Button>
                </div>
              </CardContent>
              {/* 2026-05-15 — REPLACE pool indicator. Every selected pack
                  consumes 1+ USNs from this pool when Print Batch fires.
                  If the pool is empty (the most common cause of "No
                  packs were minted"), warn the operator BEFORE they
                  click Print. */}
              {replacePool && selected.size > 0 && (
                <div
                  data-testid="resticker-replace-pool-banner"
                  className={`mx-4 mb-4 flex items-center justify-between gap-3 rounded border p-2 text-sm ${
                    replacePool.available < selected.size
                      ? 'border-rose-300 bg-rose-50 text-rose-900'
                      : replacePool.available < selected.size * 3
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {replacePool.available < selected.size ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    <span>
                      REPLACE pool: <b>{replacePool.available}</b> available
                      {' '}({replacePool.used} used of {replacePool.total} total).
                      {' '}Need ≥{Math.min(selected.size, MAX_BATCH)} for this batch.
                    </span>
                  </span>
                  {replacePool.available < selected.size && (
                    <span className="text-xs italic">
                      Import more via Admin → USN Master Import (section=&ldquo;REPLACE&rdquo;) before printing.
                    </span>
                  )}
                </div>
              )}
            </Card>

            {/* Pack table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} data-testid="select-all-checkbox" />
                        </TableHead>
                        <TableHead>Pack Barcode</TableHead>
                        <TableHead>SKU / Design</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Old MRP</TableHead>
                        <TableHead className="text-right">New MRP</TableHead>
                        <TableHead className="text-right">Δ %</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="queue-tbody">
                      {loading ? (
                        <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
                      ) : items.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No packs match the filter</TableCell></TableRow>
                      ) : items.map((it) => {
                        const delta = it.delta_pct ?? 0;
                        const isDone = !!it.restickered_at;
                        const isFailed = !!it.resticker_failed_at;
                        return (
                          <TableRow key={it.pack_barcode} data-testid={`row-${it.pack_barcode}`} className={isDone ? 'bg-green-50' : isFailed ? 'bg-red-50' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={selected.has(it.pack_barcode)}
                                onCheckedChange={() => togglePack(it.pack_barcode)}
                                disabled={isDone}
                                data-testid={`row-checkbox-${it.pack_barcode}`}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-sm">{it.pack_barcode}</TableCell>
                            <TableCell>
                              <div className="text-sm font-medium">{it.sku_code}</div>
                              <div className="text-xs text-gray-500">{it.design_name || ''}</div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{it.bin_code || '-'}</TableCell>
                            <TableCell className="text-right font-mono">{it.from_mrp ? `₹${it.from_mrp}` : '-'}</TableCell>
                            <TableCell className="text-right font-mono font-bold">{it.to_mrp ? `₹${it.to_mrp}` : '-'}</TableCell>
                            <TableCell className="text-right">
                              {delta !== 0 && (
                                <Badge variant="outline" className={delta > 0 ? 'text-green-700 border-green-300' : 'text-red-700 border-red-300'}>
                                  {delta > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                                  {delta.toFixed(1)}%
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {isDone ? <Badge className="bg-green-600">Done</Badge>
                                : isFailed ? <Badge className="bg-red-600">Failed</Badge>
                                : <Badge variant="secondary" className="bg-amber-100 text-amber-800">Pending</Badge>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {pagination.total > pagination.limit && (
                  <div className="flex justify-between items-center p-3 border-t">
                    <span className="text-sm text-gray-500">
                      Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                    </span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={pagination.skip === 0} onClick={() => setPagination((p) => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))} data-testid="prev-page">Prev</Button>
                      <Button size="sm" variant="outline" disabled={pagination.skip + pagination.limit >= pagination.total} onClick={() => setPagination((p) => ({ ...p, skip: p.skip + p.limit }))} data-testid="next-page">Next</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="batches">
            <Card>
              <CardHeader><CardTitle>Revision Batches</CardTitle><CardDescription>{batches.length} uploads</CardDescription></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Effective Date</TableHead><TableHead>SKUs</TableHead><TableHead>Uploaded By</TableHead><TableHead>Uploaded At</TableHead></TableRow></TableHeader>
                  <TableBody data-testid="batches-tbody">
                    {batches.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-gray-500">No revision batches yet</TableCell></TableRow>
                    ) : batches.map((b) => (
                      <TableRow key={b.revision_id} data-testid={`batch-${b.revision_id}`}>
                        <TableCell className="font-medium">{b.revision_name}</TableCell>
                        <TableCell>{b.effective_date}</TableCell>
                        <TableCell>{b.item_count}</TableCell>
                        <TableCell className="text-sm">{b.uploaded_by}</TableCell>
                        <TableCell className="text-sm">{b.uploaded_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Re-Sticker Audit Trail</CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-500" data-testid="audit-total">
                      {auditLoading ? 'Loading…' : `${auditTotal.toLocaleString()} event${auditTotal === 1 ? '' : 's'}`}
                    </span>
                    <Button size="sm" variant="ghost" onClick={() => { setAuditPage(1); fetchAudit(); }}
                            data-testid="audit-refresh-btn" title="Refresh">
                      <RefreshCw className={`w-4 h-4 ${auditLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mt-3">
                  <Input
                    data-testid="audit-search-usn"
                    placeholder="Search USN / pack barcode…"
                    className="md:col-span-5"
                    value={auditQuery.pack_barcode}
                    onChange={(e) => setAuditQuery(q => ({ ...q, pack_barcode: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(1); fetchAudit(); } }}
                  />
                  <Input
                    data-testid="audit-search-sku"
                    placeholder="SKU"
                    className="md:col-span-3"
                    value={auditQuery.sku}
                    onChange={(e) => setAuditQuery(q => ({ ...q, sku: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(1); fetchAudit(); } }}
                  />
                  <Input
                    data-testid="audit-search-operator"
                    placeholder="Operator (email / staff ID)"
                    className="md:col-span-3"
                    value={auditQuery.operator}
                    onChange={(e) => setAuditQuery(q => ({ ...q, operator: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setAuditPage(1); fetchAudit(); } }}
                  />
                  <Button size="sm" onClick={() => { setAuditPage(1); fetchAudit(); }} data-testid="audit-search-btn">
                    <Filter className="w-4 h-4 mr-1" /> Apply
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>USN / Pack</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>From MRP</TableHead>
                      <TableHead>To MRP</TableHead>
                      <TableHead>Operator</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="audit-tbody">
                    {auditLoading ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow>
                    ) : auditEvents.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">No events match the filters</TableCell></TableRow>
                    ) : auditEvents.map((e) => (
                      <TableRow key={e.audit_id} data-testid={`audit-${e.audit_id}`}>
                        <TableCell className="text-sm whitespace-nowrap">{e.event_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${e.event_kind === 'reprint' ? 'bg-orange-100 text-orange-900' : 'bg-emerald-100 text-emerald-900'}`}>
                            {e.event_kind === 'reprint' ? 'reprint' : 're-sticker'}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{e.usn_display || e.pack_barcode}</TableCell>
                        <TableCell>{e.sku_code || '—'}</TableCell>
                        <TableCell className="font-mono">{e.from_mrp != null ? `₹${e.from_mrp}` : '—'}</TableCell>
                        <TableCell className="font-mono font-bold">{e.to_mrp != null ? `₹${e.to_mrp}` : '—'}</TableCell>
                        <TableCell className="text-xs">{e.actor || e.restickered_by || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-stone-500" data-testid="audit-page-label">
                    Page {auditPage} of {Math.max(1, Math.ceil(auditTotal / auditPageSize))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={auditPage <= 1 || auditLoading}
                            onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                            data-testid="audit-prev-btn">
                      Previous
                    </Button>
                    <Button size="sm" variant="outline"
                            disabled={auditLoading || auditPage * auditPageSize >= auditTotal}
                            onClick={() => setAuditPage(p => p + 1)}
                            data-testid="audit-next-btn">
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reputaway" className="space-y-4">
            <Card data-testid="reputaway-card">
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <PackageOpen className="w-4 h-4" /> Re-Put-Away Report
                    </CardTitle>
                    <CardDescription>
                      USNs whose `wms_fg_inventory` row was re-stickered (price revision) and put away again into FG storage.
                      `STORED` rows are still in a bin — `PICKED` rows have moved on.
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={fetchReputaway}
                            disabled={repuLoading}
                            data-testid="reputaway-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${repuLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button size="sm" onClick={handleReputawayCSV}
                            disabled={repuLoading || (repuSummary?.total_reputaways || 0) === 0}
                            data-testid="reputaway-csv-btn">
                      <Download className="w-4 h-4 mr-2" /> Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary tiles */}
                {repuSummary ? (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="rounded-md border bg-stone-50 px-3 py-2">
                      <div className="text-xs text-stone-500">Total Re-Put-Aways</div>
                      <div className="text-lg font-bold" data-testid="repu-total">
                        {repuSummary.total_reputaways.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-md border bg-emerald-50 px-3 py-2">
                      <div className="text-xs text-emerald-700">Currently STORED</div>
                      <div className="text-lg font-bold text-emerald-900" data-testid="repu-stored">
                        {repuSummary.currently_stored.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-md border bg-blue-50 px-3 py-2">
                      <div className="text-xs text-blue-700">Already PICKED</div>
                      <div className="text-lg font-bold text-blue-900" data-testid="repu-picked">
                        {repuSummary.already_picked.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-md border bg-rose-50 px-3 py-2">
                      <div className="text-xs text-rose-700">VOID_REPLACED</div>
                      <div className="text-lg font-bold text-rose-900" data-testid="repu-void">
                        {repuSummary.void_replaced.toLocaleString()}
                      </div>
                    </div>
                    <div className="rounded-md border bg-stone-50 px-3 py-2">
                      <div className="text-xs text-stone-500">Other status</div>
                      <div className="text-lg font-bold" data-testid="repu-other">
                        {repuSummary.other_status.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={repuStatus} onValueChange={setRepuStatus}>
                      <SelectTrigger data-testid="repu-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STORED">STORED only</SelectItem>
                        <SelectItem value="PICKED">PICKED only</SelectItem>
                        <SelectItem value="VOID_REPLACED">VOID_REPLACED only</SelectItem>
                        <SelectItem value="ALL">All statuses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={repuDateFrom}
                           onChange={(e) => setRepuDateFrom(e.target.value)}
                           data-testid="repu-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={repuDateTo}
                           onChange={(e) => setRepuDateTo(e.target.value)}
                           data-testid="repu-date-to" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Search (USN, pack, bin, SKU, design)</Label>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                      <Input className="pl-8" value={repuSearch}
                             onChange={(e) => setRepuSearch(e.target.value)}
                             onKeyDown={(e) => { if (e.key === 'Enter') fetchReputaway(); }}
                             placeholder="MC123456, H06-B-04-C, …"
                             data-testid="repu-search" />
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="border rounded-md overflow-auto max-h-[60vh]">
                  {repuLoading ? (
                    <div className="text-center py-10 text-stone-500">
                      <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
                    </div>
                  ) : repuRows.length === 0 ? (
                    <div className="text-center py-10 text-stone-500"
                         data-testid="repu-empty">
                      No re-put-away rows match your filters.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-stone-50 sticky top-0">
                          <TableHead>USN</TableHead>
                          <TableHead>Bin</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>SKU / Design</TableHead>
                          <TableHead>Re-put-away At</TableHead>
                          <TableHead>By</TableHead>
                          <TableHead>Picked Slip</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {repuRows.map((r) => (
                          <TableRow key={r.fg_inv_id || r.usn_code}
                                    data-testid={`repu-row-${r.usn_code}`}>
                            <TableCell className="font-mono text-xs">{r.usn_code}</TableCell>
                            <TableCell className="font-mono text-xs">{r.bin_code || '—'}</TableCell>
                            <TableCell>
                              <Badge className={
                                r.status === 'STORED' ? 'bg-emerald-100 text-emerald-900'
                                : r.status === 'PICKED' ? 'bg-blue-100 text-blue-900'
                                : r.status === 'VOID_REPLACED' ? 'bg-rose-100 text-rose-900'
                                : 'bg-stone-100 text-stone-900'
                              }>{r.status || '—'}</Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="font-semibold">{r.sku_code || r.item_barcode || '—'}</div>
                              <div className="text-stone-500">{r.design_name || ''}</div>
                            </TableCell>
                            <TableCell className="text-xs">
                              {(r.put_away_at || r.put_away_date || '').slice(0, 19).replace('T', ' ') || '—'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.put_away_by_name || r.put_away_by || '—'}
                            </TableCell>
                            <TableCell className="text-xs font-mono">{r.picked_for_slip || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                {repuTruncated ? (
                  <p className="text-xs text-amber-700" data-testid="repu-truncated-notice">
                    Showing first 1,000 rows. Use filters or Export CSV for the full set.
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 2026-05-18 — Bin Status Report */}
          <TabsContent value="binstatus" className="space-y-4" data-testid="binstatus-tab-content">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Bin Status — Unstickered vs Clean</CardTitle>
                <p className="text-xs text-stone-500">
                  Per-bin breakdown of FG storage packs. "Unstickered" = packs with a pending rate revision that haven't been re-stickered yet. "Clean" = no rate change. Filter by bin or location, click a row to drill into the actual packs.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Bin</Label>
                    <Input data-testid="binstatus-bin-filter"
                           value={binStatusFilters.bin_code}
                           onChange={e => setBinStatusFilters(f => ({ ...f, bin_code: e.target.value }))}
                           placeholder="e.g. H01-B-01-D" />
                  </div>
                  <div>
                    <Label className="text-xs">Hall</Label>
                    <Input value={binStatusFilters.hall}
                           onChange={e => setBinStatusFilters(f => ({ ...f, hall: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Row</Label>
                    <Input value={binStatusFilters.row}
                           onChange={e => setBinStatusFilters(f => ({ ...f, row: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Rack</Label>
                    <Input value={binStatusFilters.rack}
                           onChange={e => setBinStatusFilters(f => ({ ...f, rack: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Level</Label>
                    <Input value={binStatusFilters.level}
                           onChange={e => setBinStatusFilters(f => ({ ...f, level: e.target.value }))} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-xs text-stone-700">
                      <input
                        type="checkbox"
                        data-testid="binstatus-only-unstickered"
                        checked={binStatusFilters.has_unstickered_only}
                        onChange={e => setBinStatusFilters(f => ({ ...f, has_unstickered_only: e.target.checked }))}
                      />
                      Only bins with unstickered
                    </label>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={fetchBinStatus} disabled={binStatusLoading} data-testid="binstatus-apply">
                        {binStatusLoading ? 'Loading…' : 'Apply'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={exportBinStatusCsv}
                              disabled={binStatusRows.length === 0} data-testid="binstatus-export">
                        Export CSV
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" data-testid="binstatus-total-bins">
                    Bins: {binStatusTotals.bins.toLocaleString()}
                  </Badge>
                  <Badge variant="outline" data-testid="binstatus-total-packs">
                    Total packs: {binStatusTotals.total_packs.toLocaleString()}
                  </Badge>
                  <Badge className="bg-rose-100 text-rose-800 border-rose-300" data-testid="binstatus-total-unstickered">
                    Unstickered: {binStatusTotals.total_unstickered.toLocaleString()}
                  </Badge>
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="binstatus-total-clean">
                    Clean: {binStatusTotals.total_clean.toLocaleString()}
                  </Badge>
                </div>

                <div className="border rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-100">
                      <tr>
                        <th className="text-left p-2 w-8"></th>
                        <th className="text-left p-2">Bin</th>
                        <th className="text-right p-2">Unstickered</th>
                        <th className="text-right p-2">Clean</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-left p-2">Last Put-Away</th>
                        <th className="text-left p-2">Sample SKUs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {binStatusRows.length === 0 && !binStatusLoading ? (
                        <tr><td colSpan={7} className="text-center p-6 text-stone-500">
                          No data. Apply a filter or click Apply.
                        </td></tr>
                      ) : null}
                      {binStatusRows.map((r) => {
                        const expandedMode = binStatusExpanded[r.bin_code];
                        const packsData = binStatusPacks[r.bin_code];
                        return (
                          <React.Fragment key={r.bin_code}>
                            <tr className="border-t hover:bg-stone-50" data-testid={`binstatus-row-${r.bin_code}`}>
                              <td className="p-2">
                                <Button size="sm" variant="ghost" className="h-6 px-1.5"
                                        onClick={() => toggleBinStatusRow(r.bin_code, 'all')}
                                        data-testid={`binstatus-expand-${r.bin_code}`}>
                                  {expandedMode ? '▼' : '▶'}
                                </Button>
                              </td>
                              <td className="p-2 font-mono">{r.bin_code}</td>
                              <td className="p-2 text-right">
                                {r.unstickered > 0 ? (
                                  <button className="text-rose-700 font-semibold underline"
                                          onClick={() => toggleBinStatusRow(r.bin_code, 'unstickered')}
                                          data-testid={`binstatus-unstickered-${r.bin_code}`}>
                                    {r.unstickered}
                                  </button>
                                ) : <span className="text-stone-400">0</span>}
                              </td>
                              <td className="p-2 text-right">
                                {r.clean > 0 ? (
                                  <button className="text-emerald-700 underline"
                                          onClick={() => toggleBinStatusRow(r.bin_code, 'clean')}
                                          data-testid={`binstatus-clean-${r.bin_code}`}>
                                    {r.clean}
                                  </button>
                                ) : <span className="text-stone-400">0</span>}
                              </td>
                              <td className="p-2 text-right">{r.total}</td>
                              <td className="p-2 text-xs text-stone-600">
                                {(r.last_put_away || '').slice(0, 10)}
                              </td>
                              <td className="p-2 text-xs text-stone-600 font-mono">
                                {(r.sample_skus || []).join(', ')}
                              </td>
                            </tr>
                            {expandedMode && (
                              <tr>
                                <td></td>
                                <td colSpan={6} className="p-2 bg-stone-50">
                                  {packsData?.loading ? (
                                    <div className="text-xs text-stone-500">Loading packs…</div>
                                  ) : (packsData?.packs || []).length === 0 ? (
                                    <div className="text-xs text-stone-500">No packs.</div>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead><tr className="text-stone-500">
                                          <th className="text-left p-1">Pack</th>
                                          <th className="text-left p-1">SKU</th>
                                          <th className="text-left p-1">Design</th>
                                          <th className="text-left p-1">Shade / Size</th>
                                          <th className="text-right p-1">From MRP</th>
                                          <th className="text-right p-1">To MRP</th>
                                          <th className="text-left p-1">State</th>
                                        </tr></thead>
                                        <tbody>
                                          {(packsData.packs || []).map((p) => (
                                            <tr key={p.pack_barcode} className="border-t">
                                              <td className="p-1 font-mono">{p.pack_barcode}</td>
                                              <td className="p-1 font-mono">{p.sku_code}</td>
                                              <td className="p-1">{p.design_name}</td>
                                              <td className="p-1">{p.shade} / {p.size}</td>
                                              <td className="p-1 text-right">{p.from_mrp ?? '—'}</td>
                                              <td className="p-1 text-right font-semibold">{p.to_mrp ?? '—'}</td>
                                              <td className="p-1">
                                                {p.sticker_state === 'unstickered'
                                                  ? <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px] font-semibold">UNSTICKERED</span>
                                                  : <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px]">clean</span>}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Upload CSV dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Price Revision CSV</DialogTitle>
            <DialogDescription>
              CSV columns: <code className="text-xs">sku,old_mrp,new_mrp</code>. Affected FG packs (status STORED only) will be auto-stamped as pending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Revision name</Label>
              <Input value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="e.g. May 2026 Mass Revision" data-testid="upload-name-input" />
            </div>
            <div>
              <Label>Effective date</Label>
              <Input type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} data-testid="upload-date-input" />
            </div>
            <div>
              <Label>CSV file</Label>
              <Input type="file" accept=".csv" onChange={(e) => setUploadFile(e.target.files?.[0])} data-testid="upload-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="upload-submit-btn">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print batch dialog */}
      <Dialog open={printDialogOpen} onOpenChange={(o) => { if (!printing) setPrintDialogOpen(o); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{printing ? 'Printing in progress…' : 'Confirm Bulk Print'}</DialogTitle>
            <DialogDescription>
              {printing
                ? 'Already-printed packs stay marked Done. You can abort the rest at any time.'
                : `About to print ${Math.min(selected.size, MAX_BATCH)} new stickers for ${Math.min(selected.size, MAX_BATCH)} packs in walk-order. Continue?`}
            </DialogDescription>
          </DialogHeader>

          {printing && (
            <div className="space-y-3" data-testid="print-progress-panel">
              <Progress value={printProgress.total ? (printProgress.done / printProgress.total) * 100 : 0} />
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div><div className="text-2xl font-bold">{printProgress.done}/{printProgress.total}</div><div className="text-gray-500">Processed</div></div>
                <div className="text-green-600"><div className="text-2xl font-bold">{printProgress.success}</div><div className="text-gray-500">Printed</div></div>
                <div className="text-red-600"><div className="text-2xl font-bold">{printProgress.failed}</div><div className="text-gray-500">Failed</div></div>
              </div>
              <div className="max-h-48 overflow-y-auto bg-gray-900 text-gray-100 font-mono text-xs p-2 rounded">
                {printProgress.log.map((l, i) => (
                  <div key={i} className={l.status === 'printed' ? 'text-green-400' : 'text-red-400'}>
                    {l.status === 'printed' ? '✓' : '✗'} {l.pack_barcode} {l.new_mrp ? `→ ₹${l.new_mrp}` : ''} {l.message || ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            {printing ? (
              <Button variant="destructive" onClick={handleAbort} data-testid="abort-btn"><X className="w-4 h-4 mr-2" /> Abort batch</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleStartBatchPrint} className="bg-blue-600 hover:bg-blue-700" data-testid="start-batch-print-btn"><Printer className="w-4 h-4 mr-2" /> Start Print</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Done — bin-scoped prompt (2026-05-12).
          Operator prints stickers per-bin; if a print run for one bin
          fails or the physical stickers didn't actually land, revert
          just that bin's USNs back to Pending without touching the
          rest of the queue. */}
      <Dialog open={revertDialogOpen} onOpenChange={(o) => { if (!reverting) setRevertDialogOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="revert-bin-dialog">
          <DialogHeader>
            <DialogTitle>Revert Done — Scope by Bin</DialogTitle>
            <DialogDescription>
              Enter the Bin Number whose packs need to be reverted back to <span className="font-mono">Pending</span>. Only packs that were marked Done from this bin will be affected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Bin Number</label>
              <input
                autoFocus
                value={revertBin}
                onChange={(e) => { setRevertBin(e.target.value); setRevertPreview(null); }}
                onBlur={() => fetchRevertPreview(revertBin)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchRevertPreview(revertBin); }}
                placeholder="e.g. H08-D-15-A"
                data-testid="revert-bin-input"
                className="w-full border rounded px-3 py-2 text-sm font-mono uppercase"
              />
              <p className="text-xs text-gray-500">
                Case-insensitive. Press Tab or Enter to preview the count.
              </p>
            </div>

            {revertPreviewLoading && (
              <div className="text-xs text-gray-600">Checking bin…</div>
            )}
            {revertPreview && revertPreview.count != null && (
              <div
                className={`rounded border px-3 py-2 text-sm ${
                  revertPreview.count > 0
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
                data-testid="revert-bin-preview"
              >
                {revertPreview.count > 0 ? (
                  <>
                    <strong>{revertPreview.count} pack(s)</strong> currently marked Done in this bin will be reverted to Pending.
                    {revertPreview.sample?.length > 0 && (
                      <div className="mt-1 text-xs font-mono opacity-80">
                        e.g. {revertPreview.sample.join(', ')}
                      </div>
                    )}
                  </>
                ) : (
                  <>No Done packs match this bin.</>
                )}
              </div>
            )}
            {revertPreview && revertPreview.error && (
              <div className="text-xs text-red-600">{revertPreview.error}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevertDialogOpen(false)} disabled={reverting} data-testid="revert-bin-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmBinScopedRevert}
              disabled={reverting || !revertBin.trim() || (revertPreview && revertPreview.count === 0)}
              data-testid="revert-bin-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {reverting ? 'Reverting…' : `Revert bin ${revertBin || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Reprint Stickers — printer-failure recovery. No pack state change. */}
      <Dialog open={reprintDialogOpen} onOpenChange={setReprintDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Reprint Stickers — Printer Corruption Recovery</DialogTitle>
            <DialogDescription>
              Paste or scan pack barcode(s) below (one per line). The system will pull each pack's USN list and let you tick which ones need reprinting. No DB state changes; no wage credit (corrective work).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <textarea
                placeholder="Paste pack barcodes (one per line)"
                value={reprintPackInput}
                onChange={(e) => setReprintPackInput(e.target.value)}
                rows={3}
                className="flex-1 border rounded px-3 py-2 text-sm font-mono"
                data-testid="reprint-pack-input"
              />
              <Button onClick={handleReprintLookup} disabled={reprintLoading} data-testid="reprint-lookup-btn">
                {reprintLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Look up
              </Button>
            </div>
            {!!reprintLookups.length && (
              <>
                {/* Inline USB status so the operator sees exactly why the
                    Reprint button is greyed out, and can connect without
                    closing this dialog. */}
                <div className="flex items-center gap-2 px-3 py-2 rounded border bg-gray-50" data-testid="reprint-usb-status">
                  {usbDevice ? (
                    <span className="text-sm text-green-700 flex items-center gap-2">
                      <Usb className="w-4 h-4" /> USB printer ready: {usbDevice.productName || 'connected'}
                    </span>
                  ) : (
                    <>
                      <span className="text-sm text-amber-700 flex items-center gap-2 flex-1">
                        <AlertTriangle className="w-4 h-4" /> Connect a USB printer to enable Reprint
                      </span>
                      <Button size="sm" variant="outline" onClick={handleConnectUSB} data-testid="reprint-connect-usb-btn">
                        <Usb className="w-4 h-4 mr-2" /> Connect USB
                      </Button>
                    </>
                  )}
                </div>
                <div className="border rounded p-3 max-h-80 overflow-y-auto space-y-3" data-testid="reprint-pack-list">
                {reprintLookups.map((p) => (
                  <div key={p.pack_barcode} className="border-b last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-sm font-medium">{p.pack_barcode}</span>
                      <span className="text-xs text-gray-500">
                        {p.design_name} · ₹{p.mrp} · template {p.print_template}
                        {p.already_restickered && <Badge variant="outline" className="ml-2 bg-green-50 text-green-700">Done</Badge>}
                        {p.pending && <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700">Pending</Badge>}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {p.usn_codes.map((u) => {
                        const checked = (reprintSelectedUsns[p.pack_barcode] || new Set()).has(u);
                        return (
                          <label key={u} className={`flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer ${checked ? 'bg-orange-100 border-orange-300' : 'bg-gray-50 border-gray-200'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleReprintUsn(p.pack_barcode, u)}
                              data-testid={`reprint-usn-cb-${u}`}
                            />
                            <span className="font-mono">{u}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
            {reprintProgress.total > 0 && (
              <div className="text-sm text-gray-700">
                Reprint progress: {reprintProgress.done} / {reprintProgress.total} USNs
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setReprintDialogOpen(false);
              setReprintPackInput('');
              setReprintLookups([]);
              setReprintSelectedUsns({});
              setReprintProgress({ done: 0, total: 0, log: [] });
            }}>Close</Button>
            <Button
              onClick={handleReprintExecute}
              disabled={!reprintLookups.length || !usbDevice || reprintLoading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="reprint-execute-btn"
              title={!usbDevice ? 'Connect a USB printer first' : ''}
            >
              {reprintLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Reprint Selected USNs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff capture — required before printing so the work is paid out
          against the correct operator. */}
      <Dialog open={staffPromptOpen} onOpenChange={setStaffPromptOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scan Staff ID for this batch</DialogTitle>
            <DialogDescription>
              The Staff ID you enter will be tagged against every USN printed in this batch.
              The re-sticker wage report uses this to credit work.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              autoFocus
              placeholder="Scan or type Staff ID (e.g. V0115)"
              value={batchStaffId}
              onChange={(e) => setBatchStaffId(e.target.value.trim().toUpperCase())}
              onKeyDown={async (e) => { if (e.key === 'Enter') { await handleValidateStaff(); } }}
              data-testid="batch-staff-id-input"
            />
            {batchStaffName && <div className="text-sm text-green-700">Verified: {batchStaffName}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBatchStaffId(''); setBatchStaffName(''); setStaffPromptOpen(false); }}>Cancel</Button>
            <Button onClick={handleValidateStaff} disabled={!batchStaffId || staffValidating} data-testid="confirm-staff-btn">
              {staffValidating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 — Recover Stuck Packs Dialog */}
      <Dialog open={stuckDialogOpen} onOpenChange={setStuckDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="recover-stuck-packs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Undo2 className="w-5 h-5" />
              Recover Stuck Packs
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 leading-relaxed">
              Detects packs where the NEW USN was minted via{' '}
              <code>/usn-replacement/replace-batch-stream</code> (sticker physically printed) but the
              follow-up <code>mark-restickered-bulk</code> never landed (typical when Cloudflare 520'd
              a half-completed batch). These packs still appear in the queue with{' '}
              <code>pending_revision_id</code> set. Recovery applies the missing mark in a single
              bulk_write — no double-minting, no re-print needed.
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs text-gray-700 whitespace-nowrap">Look back (hours):</Label>
              <input
                type="number"
                min={1}
                max={168}
                step={1}
                value={stuckWindowHours}
                onChange={(e) => setStuckWindowHours(Number(e.target.value) || 48)}
                disabled={stuckSubmitting}
                data-testid="stuck-window-hours-input"
                className="h-8 w-20 px-2 border border-gray-300 rounded text-sm"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={refreshStuckPreview}
                disabled={stuckSubmitting}
                data-testid="stuck-preview-refresh-btn"
                className="text-xs"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${stuckSubmitting ? 'animate-spin' : ''}`} />
                Re-preview
              </Button>
            </div>

            {stuckPreview && !stuckResult && (
              <div
                className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900 space-y-1"
                data-testid="stuck-preview-panel"
              >
                <div className="font-medium">
                  Found <strong>{stuckPreview.stuck_packs}</strong> stuck pack(s) ready to recover
                </div>
                {(stuckPreview.by_sku || []).length > 0 && (
                  <ul className="text-[11px] list-disc list-inside max-h-40 overflow-y-auto mt-1">
                    {stuckPreview.by_sku.slice(0, 50).map((row) => (
                      <li key={row.sku_code}>
                        <span className="font-mono">{row.sku_code}</span> — {row.pack_count} pack(s)
                      </li>
                    ))}
                    {stuckPreview.by_sku.length > 50 && (
                      <li className="text-amber-700">… + {stuckPreview.by_sku.length - 50} more SKU(s)</li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {stuckResult && (
              <div
                className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm text-emerald-900"
                data-testid="stuck-result-panel"
              >
                <div className="font-medium">{stuckResult.message}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setStuckDialogOpen(false)}
              disabled={stuckSubmitting}
            >
              {stuckResult ? 'Close' : 'Cancel'}
            </Button>
            {!stuckResult && (stuckPreview?.stuck_packs || 0) > 0 && (
              <Button
                onClick={handleConfirmStuckRecovery}
                disabled={stuckSubmitting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-stuck-recovery-btn"
              >
                {stuckSubmitting ? 'Recovering…' : `Recover ${stuckPreview.stuck_packs} Pack(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 (C) — Phase A "Pre-Print" prompt.
          Operator confirms the product printer + the product template
          before any TSPL is sent. Solves alignment issues that come
          from the auto-resolved template not matching the loaded roll.
          Continue is disabled until a printer is connected AND a
          template is selected. */}
      <Dialog
        open={phaseAOpen}
        onOpenChange={(open) => {
          if (!open && phaseAResolve) {
            phaseAResolve({ cancelled: true });
            setPhaseAResolve(null);
            setPhaseADevice(null);
          }
          setPhaseAOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="phase-a-dialog">
          <DialogHeader>
            <DialogTitle>Confirm printer &amp; product template</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm">
            <p className="text-gray-800">
              About to print product stickers for
              <span className="font-semibold"> {phaseAPackCount} pack(s)</span>
              {phaseAComboCount > 0 && (
                <> (including <span className="font-semibold">{phaseAComboCount} combo</span> — you'll be prompted to swap to the box-label printer after)</>
              )}.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Sticker Template</label>
              <Select value={phaseAProductTemplate} onValueChange={setPhaseAProductTemplate}>
                <SelectTrigger data-testid="phase-a-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {phaseAAvailableTemplates.map((n) => (
                    <SelectItem key={n} value={n} data-testid={`phase-a-template-option-${n}`}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Printer</label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={phaseAConnecting}
                  onClick={async () => {
                    setPhaseAConnecting(true);
                    try {
                      const dev = await printerApi.requestUSBPrinter();
                      setPhaseADevice(dev);
                      toast.success(`Product printer connected: ${dev.productName || 'USB printer'}`);
                    } catch (e) {
                      toast.error(`Connect failed: ${e.message || e}`);
                    } finally {
                      setPhaseAConnecting(false);
                    }
                  }}
                  data-testid="phase-a-connect-printer-btn"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {phaseAConnecting ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3.5 h-3.5 mr-1" />
                  )}
                  {phaseADevice ? 'Re-select Printer' : 'Connect Printer'}
                </Button>
                {phaseADevice && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-300"
                    data-testid="phase-a-printer-badge"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {phaseADevice.productName || 'USB printer'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (phaseAResolve) phaseAResolve({ cancelled: true });
                setPhaseAResolve(null);
                setPhaseADevice(null);
                setPhaseAOpen(false);
              }}
              data-testid="phase-a-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!phaseADevice || !phaseAProductTemplate}
              onClick={() => {
                if (phaseAResolve) {
                  phaseAResolve({
                    cancelled: false,
                    device: phaseADevice,
                    productTemplate: phaseAProductTemplate || null,
                  });
                }
                setPhaseAResolve(null);
                setUsbDevice(phaseADevice);
                setPhaseADevice(null);
                setPhaseAOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
              data-testid="phase-a-start-btn"
              title={
                !phaseADevice ? 'Connect a printer first'
                  : !phaseAProductTemplate ? 'Pick a product template' : ''
              }
            >
              Start Printing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 — Two-Phase Combo Sticker Prompt.
          Pauses mid-batch so the operator can swap to the box-sticker
          printer. Operator MUST explicitly connect the box-label
          printer here — Continue is disabled until done. Cancel still
          marks every pack as restickered (per user spec). */}
      <Dialog
        open={phaseDialogOpen}
        onOpenChange={(open) => {
          if (!open && phaseDialogResolve) {
            phaseDialogResolve({ cancelled: true });
            setPhaseDialogResolve(null);
            setPhaseBoxDevice(null);
          }
          setPhaseDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="phase-transition-dialog">
          <DialogHeader>
            <DialogTitle>Connect box-label printer to continue</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm">
            <p className="text-gray-800">
              Product stickers printed. Load the box-label roll on the box-sticker printer,
              then <span className="font-semibold">connect it below</span> to print the box
              stickers for <span className="font-semibold">{phaseDialogComboCount} combo pack(s)</span>.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Box Sticker Template</label>
              <Select value={phaseBoxTemplate} onValueChange={setPhaseBoxTemplate}>
                <SelectTrigger data-testid="phase-b-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {phaseBoxAvailableTemplates.map((n) => (
                    <SelectItem key={n} value={n} data-testid={`phase-b-template-option-${n}`}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={phaseBoxConnecting}
                onClick={async () => {
                  setPhaseBoxConnecting(true);
                  try {
                    const dev = await printerApi.requestUSBPrinter();
                    setPhaseBoxDevice(dev);
                    toast.success(`Box printer connected: ${dev.productName || 'USB printer'}`);
                  } catch (e) {
                    toast.error(`Connect failed: ${e.message || e}`);
                  } finally {
                    setPhaseBoxConnecting(false);
                  }
                }}
                data-testid="phase-transition-connect-printer-btn"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {phaseBoxConnecting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Printer className="w-3.5 h-3.5 mr-1" />
                )}
                {phaseBoxDevice ? 'Re-select Printer' : 'Connect Box Printer'}
              </Button>
              {phaseBoxDevice && (
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-300"
                  data-testid="phase-transition-box-printer-badge"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {phaseBoxDevice.productName || 'USB printer'}
                </Badge>
              )}
            </div>
            <p className="text-xs text-amber-700">
              Click Cancel to skip box stickers. Packs will still be marked re-stickered.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (phaseDialogResolve) phaseDialogResolve({ cancelled: true });
                setPhaseDialogResolve(null);
                setPhaseBoxDevice(null);
                setPhaseDialogOpen(false);
              }}
              data-testid="phase-transition-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!phaseBoxDevice || !phaseBoxTemplate}
              onClick={() => {
                if (phaseDialogResolve) {
                  phaseDialogResolve({
                    cancelled: false,
                    device: phaseBoxDevice,
                    boxTemplate: phaseBoxTemplate || null,
                  });
                }
                setPhaseDialogResolve(null);
                // Persist the box-printer choice for this batch session.
                setUsbDevice(phaseBoxDevice);
                setPhaseBoxDevice(null);
                setPhaseDialogOpen(false);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300"
              data-testid="phase-transition-continue-btn"
              title={
                !phaseBoxDevice ? 'Connect the box-label printer first'
                  : !phaseBoxTemplate ? 'Pick a box-sticker template' : ''
              }
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ReStickerQueue;
